import React, { useEffect, useRef, useState, useCallback } from 'react';
import styles from './forwardMessagesModal.module.css';

import { useDispatch, useSelector } from 'react-redux';
import { clearForwardMessage } from '../../reducers/wca/sales-support/sales-support-slice';
import { isTextOrNumber } from '../../utils/commonUtils';
import modulesName from '../../roles/modulesName';
import permissions from '../../roles/permissions';
import { Text } from '../Text';
import { formatMobNumber } from '../../utils/commonUtils';
import { Checkbox } from 'primereact/checkbox';
import PieChartTimer from '../../view/EnqChat/PieChartTimer';
import { ReactComponent as SendButton } from '../../assets/svgIcons/svgIcons/sendbuttonicon.svg';
import Search from './Search';
import { Dialog } from 'primereact/dialog';
import Loader from '../Loader';
import { useUtility } from '../../context/UtilityContext';
import { getAllContacts, sendMessage } from '../../reducers/wca/sales-support/sales-support-action';

const ForwardMessagesModal = ({ selectedMessages, onClose }) => {
  const customerContainerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date().getTime());
  const [selected, setSelected] = useState({});
  const [selectedChatDetails, setSelectedChatDetails] = useState([]);
  const [truncatedString, setTruncatedString] = useState('');
  const [selectedCount, setSelectedCount] = useState(0);
  const MAX_SELECTION = 5; // Maximum number of selections allowed
  const [loader, setLoader] = useState(false);
  const [page, setPage] = useState(0);

  const {
    allotedAgent,
    user,
    forwardModalAgentSearchItem,
    ticketStatusFilter,
    selectedAgent,
    agentChatStatus,
    contacts,
    hasMore,
  } = useSelector((state) => state.wcaEnqChats);
  const dispatch = useDispatch();
  const { selectedMetadataId } = useUtility();

  const SelectionLimitWarning = ({ onClose }) => {
    return (
      <div
        className="  pt-2  center flex justify-content-center"
        style={{ zIndex: 9999, background: '#fff', borderRadius: '8px' }}
      >
        <Text style={{ color: '#c79807' }}>You can select up to 5 contacts.</Text>
      </div>
    );
  };

  useEffect(() => {
    // Update the current time every second
    const intervalId = setInterval(() => {
      setCurrentTime(new Date().getTime());
    }, 1000);

    // Cleanup the interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  const isWithin24Hour = (timestamp) => {
    const enqStartedAtMs = timestamp * 1000;
    const difference = currentTime - enqStartedAtMs;
    const millisecondsIn24Hours = 24 * 60 * 60 * 1000;
    return difference <= millisecondsIn24Hours;
  };

  const toggleCheckbox = (chat) => {
    if (selectedCount === MAX_SELECTION && !selected[chat.phone]) {
      // If the maximum selection limit is reached and user tries to select more
      setSelectedCount(5);

      return;
    }

    setSelected((prevSelected) => {
      const newSelected = {
        ...prevSelected,
        [chat.phone]: !prevSelected[chat.phone],
      };

      if (newSelected[chat.phone]) {
        // If the checkbox is being checked
        setSelectedCount((prevCount) => prevCount + 1);
      } else {
        // If the checkbox is being unchecked
        setSelectedCount((prevCount) => prevCount - 1);
      }

      return newSelected;
    });
    if (
      !selectedChatDetails.some(
        // (selectedChat) => selectedChat.gsId === chat.gsId
        (selectedChat) => selectedChat.phone === chat.phone,
      )
    ) {
      // If the chat is not already in the selectedChatDetails array, add it
      setSelectedChatDetails((prevSelectedChatDetails) => [...prevSelectedChatDetails, chat]);
    } else {
      // If the chat is already in the selectedChatDetails array, remove it
      setSelectedChatDetails((prevSelectedChatDetails) =>
        prevSelectedChatDetails.filter(
          // (selectedChat) => selectedChat.gsId !== chat.gsId
          (selectedChat) => selectedChat.phone !== chat.phone,
        ),
      );
    }
  };

  useEffect(() => {
    // function to generate namesOrNumbersString
    const generateNamesOrNumbersString = (selectedChatDetails) => {
      let namesOrNumbers = [];

      selectedChatDetails
        .slice(0)
        .reverse()
        .forEach((selectedChat) => {
          const nameOrNumber = selectedChat?.whatsappName
            ? selectedChat?.whatsappName.charAt(0).toUpperCase() +
              selectedChat?.whatsappName
                .split(' ')[0] // Get only the first name
                .substring(1)
            : formatMobNumber(selectedChat?.phone);

          namesOrNumbers.push(nameOrNumber);
        });
      return namesOrNumbers.join(', ');
    };

    // Generate namesOrNumbersString
    const newNamesOrNumbersString = generateNamesOrNumbersString(selectedChatDetails);

    // Generate truncatedString
    const newTruncatedString =
      newNamesOrNumbersString.length > 25
        ? newNamesOrNumbersString.substring(0, 25) + '...'
        : newNamesOrNumbersString;
    setTruncatedString(newTruncatedString);
  }, [selectedChatDetails]);

  const handleShare = async () => {
    const phoneNumbers = [];
    selectedChatDetails.forEach((item) => {
      phoneNumbers.push(item.phone);
    });

    const apiPromises = [];

    const ApiCall = async (number, messages) => {
      setLoader(true);
      for (let i = 0; i < messages?.length; i++) {
        let message = messages[i];
        let payload = {};

        switch (message.message.messageType) {
          case 'text':
            payload = {
              messaging_product: 'whatsapp',
              recipient_type: 'individual',
              to: number,
              type: 'text',
              text: {
                body: message.message.text,
              },
            };
            break;
          case 'image':
            payload = {
              messaging_product: 'whatsapp',
              recipient_type: 'individual',
              to: number,
              type: 'image',
              text: {
                caption: message.message.text,
                link: message.message.url,
              },
            };
            break;
          case 'video':
            payload = {
              messaging_product: 'whatsapp',
              recipient_type: 'individual',
              to: number,
              type: 'video',
              text: {
                caption: message.message.text,
                link: message.message.url,
              },
            };
            break;
          case 'audio':
            payload = {
              messaging_product: 'whatsapp',
              recipient_type: 'individual',
              to: number,
              type: 'audio',
              text: {
                link: message.message.url,
              },
            };
            break;
          case 'file':
            payload = {
              messaging_product: 'whatsapp',
              recipient_type: 'individual',
              to: number,
              type: 'file',
              text: {
                caption: message.message.text,
                link: message.message.url,
                filename: message.message.name,
              },
            };
            break;

          default:
            break;
        }
        try {
          setLoader(true);
          // Make the API call
          await dispatch(
            sendMessage({
              metadataid: selectedMetadataId,
              data: payload,
            }),
          );
        } catch (error) {
          // Handle errors
          console.error('Error sending message:', error);
        }
      }
    };

    for (let i = 0; i < phoneNumbers.length; i++) {
      apiPromises.push(ApiCall(phoneNumbers[i], selectedMessages));
    }

    try {
      await Promise.all(apiPromises);
    } catch (error) {
      console.error('Error sending messages:', error);
    }

    dispatch(clearForwardMessage());

    onClose(); // Close the modal after sharing
    setLoader(false);
  };

  const fetchChats = useCallback(
    async (isInitial = true, resetPage = true) => {
      if (!selectedMetadataId) return;

      if (isInitial && resetPage) {
        setPage(0);
      }
      setLoading(true);

      try {
        const filterData = [];
        if (agentChatStatus !== 'all')
          filterData.push({ key: 'agentChatStatus', value: agentChatStatus });
        if (ticketStatusFilter) filterData.push({ key: 'ticketStatus', value: ticketStatusFilter });
        if (selectedAgent) filterData.push({ key: 'agentId', value: selectedAgent });

        await dispatch(
          getAllContacts({
            metadataid: selectedMetadataId,
            pageNo: isInitial && resetPage ? 0 : page,
            limit: 30,
            startDate: null,
            endDate: null,
            filterData,
            globalFilterValue: forwardModalAgentSearchItem,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Failed to fetch chats:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      selectedMetadataId,
      allotedAgent,
      page,
      agentChatStatus,
      ticketStatusFilter,
      selectedAgent,
      forwardModalAgentSearchItem,
      dispatch,
    ],
  );

  // --- CHANGE: Effect for initial data fetch and refetch on filter change ---
  useEffect(() => {
    fetchChats(true, true);
  }, [
    selectedMetadataId,
    allotedAgent,
    agentChatStatus,
    ticketStatusFilter,
    selectedAgent,
    forwardModalAgentSearchItem,
  ]);

  const loadMoreCustomersChat = useCallback(() => {
    if (!hasMore || loading) return;
    setPage((prevPage) => prevPage + 1);
  }, [hasMore, loading]);

  useEffect(() => {
    if (page > 0) {
      fetchChats(false, false);
    }
  }, [page, fetchChats]);

  const handleScroll = () => {
    if (customerContainerRef.current && !loading && hasMore) {
      const { scrollTop, clientHeight, scrollHeight } = customerContainerRef.current;

      const tolerance = 1;

      if (scrollTop + clientHeight + tolerance >= scrollHeight) {
        loadMoreCustomersChat();
      }
    }
  };

  return (
    <Dialog
      visible={true}
      onHide={onClose}
      showHeader={false}
      modalClassName={` `}
      className={`w-11 md:w-6 lg:w-5 xl:w-3 ${styles.modal} `}
      contentClassName={` ${styles.content}  `}
    >
      {/* <div className="w-full border-1 p-0" > */}
      <Loader visible={loader} />
      <div
        className={` ${styles.modalContainer}`}
        ref={customerContainerRef}
        onScroll={handleScroll}
      >
        <div className={` ${styles.header} `}>
          <i
            className={`pi pi-times`}
            style={{ fontSize: '1.125rem', color: '#e2e2e2' }}
            onClick={onClose}
          ></i>
          <Text type={'heading'} style={{ color: '#e2e2e2' }}>
            Forward messages to
          </Text>
        </div>
        {loading && (
          <div className="text-center">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }}></i>
          </div>
        )}
        <div style={{ padding: '8px 8px 0px 8px', background: '#fff' }}>
          <Search />
        </div>
        <div className="flex justify-content-center w-full">
          {selectedCount === 5 && <SelectionLimitWarning />}
        </div>

        <div className={`${styles.userChatContainer}`}>
          {contacts?.length !== 0
            ? contacts.map((chat, i) => {
                // console.log('chat', chat);
                return (
                  <div className={`${styles.userChat}`} key={i}>
                    {chat !== null && (
                      <div className={`${styles.userChatInfo}`}>
                        <div className="pt-1 ">
                          <Checkbox
                            checked={selected[chat.phone]}
                            onChange={() => toggleCheckbox(chat)}
                            disabled={!isWithin24Hour(chat.lastMessageTimestamp)}
                            style={{
                              opacity: !isWithin24Hour(chat.lastMessageTimestamp) ? 0.5 : 1,
                            }}
                            className={`${styles['p-checkbox-box']} ${styles['p-checkbox']}`}
                          />
                        </div>

                        <div className="  w-full flex justify-content-between ">
                          <Text
                            type={'heading'}
                            cust="ch-limit-30"
                            style={{
                              fontWeight: 500,
                              opacity: !isWithin24Hour(chat.lastMessageTimestamp) ? 0.5 : 1,
                            }}
                          >
                            <span>
                              {chat?.whatsappName
                                ? chat?.whatsappName.charAt(0).toUpperCase() +
                                  chat?.whatsappName.substring(1)
                                : formatMobNumber(chat?.phone)}
                            </span>
                          </Text>
                          <div className="flex flex-column align-items-end">
                            <div
                              className="mb-1 ml-2 flex flex-column  justify-content-center"
                              style={{
                                width: '20px',
                                height: '20px',
                                border: '1px solid rgba(0, 0, 0, 0.7)',
                                borderRadius: '50%',
                                padding: '2px',
                              }}
                            >
                              <PieChartTimer
                                currentTime={currentTime}
                                startTimestamps={chat.lastMessageTimestamp * 1000}
                                duration={24 * 60 * 60 * 1000}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            : !loading && (
                <div className="h-full flex flex-column align-items-center justify-content-center">
                  <span> No customers found</span>
                </div>
              )}
        </div>

        {selectedChatDetails.length > 0 && (
          <div className={`${styles.shareContainer}`}>
            <div className="flex flex-column w-10 ">
              <div className={`${styles.selectedCustomersContainer}`}>
                <span className={`${styles.selectedCustomer} mt-1 px-1`}>{truncatedString}</span>
              </div>

              {selectedChatDetails.length > 0 && (
                <span className={`${styles.selectedCustomer} mt-1 px-1`}>
                  Selected : {selectedChatDetails.length}
                </span>
              )}
            </div>
            <div onClick={handleShare} className="pr-2" style={{ cursor: 'pointer' }}>
              {' '}
              <SendButton />{' '}
            </div>
          </div>
        )}
      </div>
      {/* </div> */}
    </Dialog>
  );
};

export default ForwardMessagesModal;
